import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";

/** Width the sidebar ships at, and falls back to when nothing is stored. */
export const SIDEBAR_DEFAULT_WIDTH = 240;
/** Narrow enough to give the grid room, wide enough for the date chips row. */
export const SIDEBAR_MIN_WIDTH = 200;
/** Wide enough for long plan/tag names without swallowing the grid. */
export const SIDEBAR_MAX_WIDTH = 560;

/** Per-browser, shared by every host so the panel doesn't jump between pages. */
const STORAGE_KEY = "otus:records-sidebar-width";
const KEYBOARD_STEP = 16;
const KEYBOARD_STEP_MULTIPLIER = 4;

function clampWidth(width: number): number {
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width));
}

/** The remembered width, for hosts that lay out around a hidden sidebar. */
export function readStoredWidth(): number {
  try {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    // Covers null, "", and garbage — all become NaN and fall back.
    return Number.isFinite(stored) && stored > 0
      ? clampWidth(Math.round(stored))
      : SIDEBAR_DEFAULT_WIDTH;
  } catch {
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

function writeStoredWidth(width: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    // Private-mode / quota failures are not worth surfacing — the width just
    // won't survive the reload.
  }
}

interface SidebarWidthHandleProps {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
}

/**
 * Owns the desktop sidebar's width: drag-to-resize from the right edge,
 * keyboard resizing for the focused handle, and persistence across sessions.
 * See [[records-sidebar.spec]] US-004.
 */
export function useSidebarWidth(): {
  width: number;
  isResizing: boolean;
  handleProps: SidebarWidthHandleProps;
} {
  const [width, setWidth] = useState(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  // Mirrors `width` so the drag end can persist without re-creating handlers
  // on every pointermove.
  const widthRef = useRef(width);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
  } | null>(null);

  const applyWidth = useCallback((next: number) => {
    const clamped = clampWidth(Math.round(next));
    widthRef.current = clamped;
    setWidth(clamped);
  }, []);

  const commitWidth = useCallback((next: number) => {
    const clamped = clampWidth(Math.round(next));
    widthRef.current = clamped;
    setWidth(clamped);
    writeStoredWidth(clamped);
  }, []);

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: widthRef.current,
    };
    setIsResizing(true);
    // Capture so a fast drag that outruns the 8px handle keeps sending moves.
    // jsdom has no pointer capture, hence the guard.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }, []);

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      applyWidth(drag.startWidth + (event.clientX - drag.startX));
    },
    [applyWidth],
  );

  const endDrag = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      setIsResizing(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      commitWidth(widthRef.current);
    },
    [commitWidth],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const step = event.shiftKey
        ? KEYBOARD_STEP * KEYBOARD_STEP_MULTIPLIER
        : KEYBOARD_STEP;
      switch (event.key) {
        case "ArrowLeft":
          commitWidth(widthRef.current - step);
          break;
        case "ArrowRight":
          commitWidth(widthRef.current + step);
          break;
        case "Home":
          commitWidth(SIDEBAR_MIN_WIDTH);
          break;
        case "End":
          commitWidth(SIDEBAR_MAX_WIDTH);
          break;
        default:
          return;
      }
      event.preventDefault();
    },
    [commitWidth],
  );

  const onDoubleClick = useCallback(() => {
    commitWidth(SIDEBAR_DEFAULT_WIDTH);
  }, [commitWidth]);

  // While dragging, the pointer regularly leaves the handle. Force the resize
  // cursor and kill text selection document-wide so a fast drag doesn't
  // select the grid behind it.
  useEffect(() => {
    if (!isResizing) return;
    const { body } = document;
    const previousCursor = body.style.cursor;
    const previousUserSelect = body.style.userSelect;
    body.style.cursor = "col-resize";
    body.style.userSelect = "none";
    return () => {
      body.style.cursor = previousCursor;
      body.style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  return {
    width,
    isResizing,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
      onDoubleClick,
    },
  };
}
